import { useState, useEffect } from "react";
import { Users, Mail, FileText, RefreshCw, Download, Lock } from "lucide-react";

function LoginGate({ onAuth }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem("admin_token", data.token);
        onAuth(data.token);
      } else {
        setError("Invalid credentials");
      }
    } catch {
      setError("Connection error");
    }
    setLoading(false);
  };

  return (
    <main className="bg-[#0F1A2E] min-h-screen flex items-center justify-center">
      <div className="w-full max-w-sm p-8 border border-slate-800 bg-[#162A45]">
        <div className="flex items-center gap-3 mb-8">
          <Lock size={16} className="text-[#D4AF37]" />
          <h1 className="text-white text-sm uppercase tracking-[3px]">Admin Access</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            data-testid="admin-username"
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Email"
            required
            className="w-full bg-[#0B1221] border border-slate-700 focus:border-[#D4AF37] text-white text-sm px-4 py-3 outline-none transition-colors placeholder:text-slate-600"
          />
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="w-full bg-[#0B1221] border border-slate-700 focus:border-[#D4AF37] text-white text-sm px-4 py-3 outline-none transition-colors placeholder:text-slate-600"
          />
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-[#0B1221] text-xs font-bold uppercase tracking-widest px-4 py-3 hover:bg-[#F59E0B] transition-colors disabled:opacity-60"
          >
            {loading ? "..." : "Login"}
          </button>
        </form>
      </div>
    </main>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [analytics, setAnalytics] = useState({ pdf_downloads: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("contacts");

  useEffect(() => {
    const saved = sessionStorage.getItem("admin_token");
    if (saved) {
      fetch(`/api/admin/verify?token=${saved}`)
        .then((r) => r.json())
        .then((d) => { if (d.valid) setToken(saved); else sessionStorage.removeItem("admin_token"); })
        .catch(() => {});
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, l, s, a] = await Promise.all([
        fetch("/api/contacts").then((r) => r.json()).catch(() => ({ contacts: [], total: 0 })),
        fetch("/api/leads").then((r) => r.json()).catch(() => ({ leads: [], total: 0 })),
        fetch("/api/newsletter/subscribers").then((r) => r.json()).catch(() => ({ subscribers: [], total: 0 })),
        fetch("/api/analytics").then((r) => r.json()).catch(() => ({ pdf_downloads: 0 })),
      ]);
      setContacts(c.contacts || []);
      setLeads(l.leads || []);
      setSubscribers(s.subscribers || []);
      setAnalytics(a);
    } catch {
      /* silent */
    }
    setLoading(false);
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  if (!token) return <LoginGate onAuth={setToken} />;

  const tabs = [
    { key: "contacts", label: "Contact Enquiries", icon: Mail, data: contacts },
    { key: "leads", label: "Lead Downloads", icon: FileText, data: leads },
    { key: "subscribers", label: "Blog Subscribers", icon: Users, data: subscribers },
  ];

  const active = tabs.find((t) => t.key === activeTab);

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
  };

  return (
    <main className="bg-[#0F1A2E] min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-2xl text-white font-semibold tracking-wide" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Admin Dashboard
            </h1>
            <p className="text-slate-500 text-xs mt-1 uppercase tracking-widest">Advaltis Consulting</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-refresh"
              onClick={fetchData}
              disabled={loading}
              className="text-slate-400 hover:text-[#D4AF37] transition-colors disabled:opacity-40"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="text-slate-500 text-xs uppercase tracking-widest hover:text-red-400 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              data-testid={`admin-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`p-5 border text-left transition-all duration-200 ${
                activeTab === tab.key
                  ? "border-[#D4AF37]/40 bg-[#162A45]"
                  : "border-slate-800 bg-[#0B1221] hover:border-slate-700"
              }`}
            >
              <tab.icon size={16} className={activeTab === tab.key ? "text-[#D4AF37]" : "text-slate-600"} />
              <p className="text-2xl text-white font-semibold mt-3">{tab.data.length}</p>
              <p className="text-slate-500 text-xs uppercase tracking-widest mt-1">{tab.label}</p>
            </button>
          ))}
          <div className="p-5 border border-slate-800 bg-[#0B1221]" data-testid="admin-pdf-downloads">
            <Download size={16} className="text-slate-600" />
            <p className="text-2xl text-white font-semibold mt-3">{analytics.pdf_downloads}</p>
            <p className="text-slate-500 text-xs uppercase tracking-widest mt-1">PDF Downloads</p>
          </div>
        </div>

        {/* Data Table */}
        <div className="border border-slate-800 bg-[#162A45]">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-white text-sm font-medium">{active?.label} ({active?.data.length})</h2>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading...</div>
          ) : active?.data.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No records yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="border-b border-slate-800">
                    {activeTab === "contacts" && (
                      <>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Name</th>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Email</th>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Message</th>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Date</th>
                      </>
                    )}
                    {activeTab === "leads" && (
                      <>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Email</th>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Source</th>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Date</th>
                      </>
                    )}
                    {activeTab === "subscribers" && (
                      <>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Email</th>
                        <th className="text-left text-slate-500 text-xs uppercase tracking-wider px-5 py-3">Date</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {active?.data.map((item, i) => (
                    <tr key={i} className="border-b border-slate-800/50 hover:bg-[#0B1221]/50">
                      {activeTab === "contacts" && (
                        <>
                          <td className="px-5 py-3 text-white">{item.name}</td>
                          <td className="px-5 py-3 text-[#D4AF37]">{item.email}</td>
                          <td className="px-5 py-3 text-slate-400 max-w-xs truncate">{item.message}</td>
                          <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{item.created_at?.split("T")[0]}</td>
                        </>
                      )}
                      {activeTab === "leads" && (
                        <>
                          <td className="px-5 py-3 text-[#D4AF37]">{item.email}</td>
                          <td className="px-5 py-3 text-slate-400">{item.source || "framework-download"}</td>
                          <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{item.created_at?.split("T")[0]}</td>
                        </>
                      )}
                      {activeTab === "subscribers" && (
                        <>
                          <td className="px-5 py-3 text-[#D4AF37]">{item.email}</td>
                          <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{item.created_at?.split("T")[0]}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
